import {Component, ElementRef, Inject, ViewChild, OnInit, DestroyRef, inject} from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { of } from 'rxjs';
import { filter, tap, switchMap } from 'rxjs/operators';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ItemDetail} from '@emm/core/models/item-detail.model';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ItemService} from '@emm/core/services/item.service';
import {TagService} from '@emm/core/services/tag.service';
import {NotificationService} from '@emm/shared/services/notification.service';

@Component({
    selector: 'emm-add-item-to-menu-dialog',
    styleUrls: ['./add-item-to-menu-dialog.component.scss'],
    templateUrl: './add-item-to-menu-dialog.component.html',
    standalone: false
})
export class AddItemToMenuDialogComponent implements OnInit {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    addGlobal: FormControl = new FormControl();
    displayAddGlobal = false;
    emergencyTag: string;
    errorMessage: string;
    existingItem: ItemDetail = null;
    input: FormControl = new FormControl();
    isItemSelected = false;
    isReadOnly$ = this.authFacade.isReadOnly$;
    items: ItemDetail[] = [];
    options: ItemDetail[];
    removable = true;
    selectable = true;
    tagExists = false;
    visible = true;

    @ViewChild('itemInput') itemInput: ElementRef<HTMLInputElement>;

    constructor(
        private authFacade: AuthFacadeService,
        public dialogRef: MatDialogRef<AddItemToMenuDialogComponent>,
        private itemService: ItemService,
        private tagService: TagService,
        private notificationService: NotificationService,
        @Inject(MAT_DIALOG_DATA) public data: any) {
        const exp = new RegExp('[$&+,:;=?@#|\'<>.^*()%!-]');
        const itemTypeFilter = data.itemTypeFilter;
        this.displayAddGlobal = data.displayAddGlobal || false;
        this.emergencyTag = data.emergencyTag || null;
        this.input.valueChanges
            .pipe(
                filter((value: any) => typeof value === 'string'),
                switchMap((value: any) => {
                    if (!value || value.length < 2 || value === 'input') {
                        return of(null).pipe(
                            tap((_) => {
                                this.options = [];
                            })
                        );
                    }
                    if (exp.test(value)) {
                        let newValue;
                                               for (let i = 0; i < value.length; i++) {
                            if (exp.test(value[i])) {
                                newValue = value.replace(value[i], '');
                                value = newValue;
                            }
                        }
                    }

                    return this.itemService.filterItems(value, itemTypeFilter, 50).pipe(
                        tap((results) => {
                            const exactMatch = results.filter((i) => i.tag === value || i.name === value);
                            if (exactMatch.length > 0) {
                                this.options = [];
                                this.input.setValue(exactMatch[0]);
                                this.isItemSelected = true;
                            } else {
                                this.options = results;
                            }
                        })
                    );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }



    ngOnInit() {}


    onPaste(event: ClipboardEvent) {
        const clipboardData = event.clipboardData;
        const pastedText = clipboardData.getData('text');
        this.isItemSelected = true;
        const count = this.items.filter((item: ItemDetail) => item.tag === pastedText || item.name === pastedText).length;
        if (count === 0) {
            try {
                const pastSubscription = this.itemService.filterItems(pastedText, this.data.itemTypeFilter, 50)
                    .pipe(
                        takeUntilDestroyed(this.destroyRef)
                    )
                    .subscribe((results) => {
                    const exactMatch = results.filter((i) => i.tag === pastedText || i.name === pastedText);
                    exactMatch.length === 1 ? this.items.push(exactMatch[0]) :
                        this.notificationService.error('There was an error pasting. Please try typing your item name or tag instead.');;
                    this.itemInput.nativeElement.value = '';
                    pastSubscription.unsubscribe();
                });
            } catch (e) {
                this.notificationService.error('There was an error pasting. Please try typing your item name or tag instead.');
                this.itemInput.nativeElement.value = '';
            }
        } else {
            this.notificationService.error('Item already exists in the selected list. Please try again.');
            this.itemInput.nativeElement.value = '';
        }
    }


    displayFn(option): string | undefined {
        return option ? option.name : undefined;
    }



    cancelAddItem(): void {
        this.dialogRef.close();
    }



    addItem(): void {
        const result = {
            addGlobal: this.addGlobal.value || false,
            value: this.items
        };
        this.dialogRef.close(result);
    }



    public itemSelected(e: MatAutocompleteSelectedEvent): void {
        this.isItemSelected = true;
        const count = this.items.filter((item: ItemDetail) => item.tag === e.option.value.tag).length;
        if (count === 0) {
            this.items.push(e.option.value);
            this.input.setValue(null);
            this.itemInput.nativeElement.value = '';
            if (this.emergencyTag != null && this.items.length > 0) {
                const item = this.items[this.items.length - 1];
                const tag = this.items[this.items.length - 1].tag;
                this.tagService
                    .getEmergencyTagAndItemTag(this.emergencyTag, tag)
                    .pipe(
                        takeUntilDestroyed(this.destroyRef)
                    )
                    .subscribe((res) => {
                        this.tagExists = res.response;
                        this.existingItem = item;
                        this.errorMessage = 'Tag already exists! Create a new one.';
                    });
            }
        } else {
            this.existingItem = e.option.value;
            this.tagExists = true;
            this.errorMessage = 'Tag already exists in the selected list! Select a new one';
            this.items.push(e.option.value);
            this.input.setValue(null);
            this.itemInput.nativeElement.value = '';
        }
    }



    public removeItem(item: ItemDetail): void {
        const index = this.items.indexOf(item);
        if (index >= 0) {
            if (this.tagExists && this.existingItem.tag === item.tag) {
                this.tagExists = false;
            }
            this.items.splice(index, 1);
        }
    }



}
